import {
  AHText,
  AHTextFonts,
  Box,
  Circle,
  useLayoutContext,
  useScaleFactor,
} from "arkh";
import React, { useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import Color from "color";
import { graphql, useFragment } from "react-relay";
import { WidgetEmployeeScheduleShiftBar$key } from "./__generated__/WidgetEmployeeScheduleShiftBar.graphql";

export const WidgetEmployeeScheduleShiftBar = React.memo(
  WidgetEmployeeScheduleShiftBarCmp
);
function WidgetEmployeeScheduleShiftBarCmp(props: {
  shift: WidgetEmployeeScheduleShiftBar$key;
  dayStartMinutes: number;
  boxWidth: number;
  isDoubleHeight: boolean;
  showTime: boolean;
}) {
  const shift = useFragment(
    graphql`
      fragment WidgetEmployeeScheduleShiftBar on EmployeeShift {
        status
        formattedStartAtTime
        formattedEndAtTime
        durationSeconds
        startAtSecondsFromMidnight

        scheduleType {
          title
          color
        }
        secondaryScheduleType {
          title
          color
        }
      }
    `,
    props.shift
  );

  const start = shift.startAtSecondsFromMidnight / 60 - props.dayStartMinutes;
  const width = shift.durationSeconds / 60;

  const isActive = useMemo(() => shift.status !== "PAST", [shift.status]);
  const typeColor = shift.scheduleType?.color;
  const typeDesription = shift.scheduleType?.title ?? null;
  const color = useMemo(
    () => Color(typeColor ?? "#000").mix(Color("#FFF"), isActive ? 0 : 0.8),
    [isActive, typeColor]
  );

  const style = useMemo<StyleProp<ViewStyle>>(
    () => ({
      height: "100%",
      backgroundColor: color.toString(),
      width: (width / 30) * props.boxWidth,
      left: Math.floor((start / 30) * props.boxWidth),
      top: 0,
      bottom: 0,
      position: "absolute",
      justifyContent: "center",
      flexDirection: "column",
    }),
    [color, props.boxWidth, start, width]
  );

  return (
    <Box cornerRadius="small" style={style}>
      <View style={{ opacity: isActive ? 1 : 0.3, overflow: "hidden" }}>
        {props.isDoubleHeight ? (
          <>
            <ShiftPartContent
              color={color}
              showTime
              description={typeDesription}
              endAt={shift.formattedEndAtTime}
              startAt={shift.formattedStartAtTime}
              secondaryScheduleType={shift.secondaryScheduleType}
            />
            <ShiftPartContent
              color={color}
              showTime={false}
              description={typeDesription}
              endAt={shift.formattedEndAtTime}
              startAt={shift.formattedStartAtTime}
              secondaryScheduleType={shift.secondaryScheduleType}
            />
          </>
        ) : (
          <ShiftPartContent
            color={color}
            showTime={props.showTime}
            description={typeDesription}
            endAt={shift.formattedEndAtTime}
            startAt={shift.formattedStartAtTime}
            secondaryScheduleType={shift.secondaryScheduleType}
          />
        )}
      </View>
    </Box>
  );
}
const ShiftPartContent = React.memo(ShiftPartContentC);
function ShiftPartContentC(props: {
  showTime: boolean;
  color: Color;
  startAt: string;
  endAt: string;
  description: string | null;
  secondaryScheduleType: Readonly<{
    color: string | null;
    title: string;
  }> | null;
}) {
  const sf = useScaleFactor();
  const layoutContext = useLayoutContext();
  return (
    <>
      <AHText
        type="detail"
        numberOfLines={1}
        ellipsizeMode="clip"
        style={{
          marginLeft: Math.round(3 * sf),
          fontSize: Math.round(10 * sf),
          display: props.showTime ? "flex" : "none",
        }}
        font={AHTextFonts.BOLDER}
        color={props.color.darken(0.1).isLight() ? "darkerText" : "lightText"}
      >
        {props.startAt}-{props.endAt}
      </AHText>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginLeft: Math.round(3 * sf),
          display: props.showTime ? "none" : "flex",
        }}
      >
        <AHText
          type="detail"
          numberOfLines={1}
          style={{
            fontSize: Math.round(12 * sf),
            flexShrink: 0,
          }}
          font={AHTextFonts.BOLD}
          color={props.color.darken(0.1).isLight() ? "darkerText" : "lightText"}
        >
          {props.description}
        </AHText>
        {props.secondaryScheduleType ? (
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginLeft: Math.round(layoutContext.space.xsmall * sf),
              flexShrink: 1,
            }}
            color="surface-primary"
            cornerRadius="large"
            space="xxsmall"
            spaceHorizontal="xsmall"
          >
            <Circle
              size="dot"
              style={{
                backgroundColor: Color(
                  props.secondaryScheduleType.color ?? "#000"
                ).toString(),
              }}
            />
            <AHText
              spaceLeft="xsmall"
              type="detail"
              numberOfLines={1}
              ellipsizeMode="tail"
              color="darkerText"
            >
              {props.secondaryScheduleType.title}
            </AHText>
          </Box>
        ) : null}
      </View>
    </>
  );
}
